import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import classes from './createCourse.module.css';
import Header from '../../components/standartComponents/header';
import { getCSRFToken, fetchCSRFToken } from '../../lib/csrf';
import { useTranslation } from 'react-i18next';

const LEVEL_BADGE = { 1: 'A1', 2: 'A0', 3: 'A2', 4: 'B1', 5: 'B2', 6: 'C1', 7: 'pre-A1' };

// Хелпер: береме перший levels[] і маппимо на назву
const getLevelBadge = (levels) => {
  if (!levels?.length) return null;
  // Якщо кілька (напр. [5,6]) — показуємо діапазон
  if (levels.length > 1) {
    return `${LEVEL_BADGE[levels[0]]}–${LEVEL_BADGE[levels[levels.length - 1]]}`;
  }
  return LEVEL_BADGE[levels[0]] || null;
};

const TOPICS = [
  { id: 'work',     name: 'Робота',      en: 'Work English',    desc: 'Співбесіди, зустрічі, листування, робочі діалоги.',   icon: '💼', bg: '#EEF0FF', purposeId: 2 },
  { id: 'travel',   name: 'Подорожі',    en: 'Travel English',  desc: 'Аеропорт, готель, кафе, знайомства за кордоном.',     icon: '✈️', bg: '#FEF3C7', purposeId: 1 },
  { id: 'study',    name: 'Навчання',    en: 'Study English',   desc: 'Освітні теми, пояснення, академічна лексика.',        icon: '🎓', bg: '#E0E3FF', purposeId: 3 },
  { id: 'social',   name: 'Спілкування', en: 'Social English',  desc: 'Small talk, знайомства, щоденні розмови.',            icon: '💬', bg: '#ECFDF5', purposeId: 4 },
  { id: 'personal', name: 'Для себе',    en: 'Personal English',desc: 'Теми з життя, хобі та легка практика.',               icon: '🌿', bg: '#FFF1F2', purposeId: 5 },
  { id: 'tech',     name: 'News & Tech', en: 'Tech English',    desc: 'Сучасні теми, технології, новини та обговорення.',    icon: '📱', bg: '#F0F9FF', purposeId: 5 },
  { id: 'health',   name: "Здоров'я",    en: 'Health English',  desc: 'Лікар, самопочуття, аптека, базові ситуації.',        icon: '🩺', bg: '#ECFDF5', purposeId: 5 },
  { id: 'leisure',  name: 'Дозвілля',    en: 'Leisure English', desc: 'Плани, вихідні, розваги та зустрічі.',                icon: '🎯', bg: '#FFFBEB', purposeId: 5 },
];

const NEXT_LEVEL = { A0: 'A1', A1: 'A2', A2: 'B1', B1: 'B2', B2: 'C1', C1: 'C2' };

const CreateCourse = ({ authUser }) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const [selTopic, setSelTopic]     = useState(null);
  const [selLevel, setSelLevel]     = useState(null); // зберігає id з API
  const [isLoading, setIsLoading]   = useState(false);
  const [error, setError]           = useState(null);

  const [levels, setLevels]           = useState([]);
  const [levelsLoading, setLevelsLoading] = useState(true);
  const [levelsError, setLevelsError]   = useState(null);

  // Завантаження рівнів при монтуванні та при зміні мови
  useEffect(() => {
    const fetchLevels = async () => {
      setLevelsLoading(true);
      setLevelsError(null);
      try {
        const res = await fetch('/api/v1/language-level-questionnaire', {
          credentials: 'include',
          headers: {
            'Accept-Language': i18n.language || 'uk',
          },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        console.log("levels data:", data);
        const sorted = [...data.results];
        const preA1Idx = sorted.findIndex(l => (l.levels || []).includes(7));
        if (preA1Idx > 0) {
          const [preA1] = sorted.splice(preA1Idx, 1);
          sorted.unshift(preA1);
        }
        
        setLevels(sorted);
      } catch (err) {
        console.error('Error fetching levels:', err);
        setLevelsError('Не вдалося завантажити рівні.');
      } finally {
        setLevelsLoading(false);
      }
    };

    fetchLevels();
  }, [i18n.language]); // рефетч при зміні мови

  // Скидаємо вибраний рівень якщо його більше немає після рефетчу
  useEffect(() => {
    if (selLevel && levels.length > 0) {
      const stillExists = levels.some(l => l.id === selLevel);
      if (!stillExists) setSelLevel(null);
    }
  }, [levels]);

  const topicObj = TOPICS.find(t => t.id === selTopic);
  const levelObj = levels.find(l => l.id === selLevel);
  const nextLvl  = ''; // рівні тепер з API, NEXT_LEVEL не застосовується

  const handleCreate = async () => {
    if (!selTopic || !selLevel || isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      let csrfToken = getCSRFToken();
      if (!csrfToken) {
        await fetchCSRFToken();
        csrfToken = getCSRFToken();
      }

      const headers = {
        'Content-Type': 'application/json',
        ...(csrfToken && { 'X-CSRFToken': csrfToken }),
      };

      // 1. Отримати поточний профіль
      const profileRes = await fetch('/api/v1/user-profile/current', {
        method: 'GET',
        credentials: 'include',
        headers,
      });
      if (!profileRes.ok) throw new Error(`Failed to fetch profile: HTTP ${profileRes.status}`);
      const profile = await profileRes.json();

      const existingLevels   = profile.english_levels   || [];
      const existingPurposes = profile.english_purposes || [];

      // levelObj.levels — масив числових id рівнів з API
      const newLevelInts = levelObj?.levels || [];
      const newPurposeId = topicObj.purposeId;

      // 2. Merge levels (всі числові id з вибраного рівня)
      const mergedLevels = [
        ...existingLevels,
        ...newLevelInts.filter(lvl => !existingLevels.includes(lvl)),
      ];

      // 3. Merge purposes
      const mergedPurposes = existingPurposes.includes(newPurposeId)
        ? existingPurposes
        : [...existingPurposes, newPurposeId];

      const levelsChanged   = newLevelInts.some(lvl => !existingLevels.includes(lvl));
      const purposesChanged = !existingPurposes.includes(newPurposeId);

      if (levelsChanged || purposesChanged) {
        const patchRes = await fetch('/api/v1/user-profile/current', {
          method: 'PATCH',
          credentials: 'include',
          headers,
          body: JSON.stringify({
            english_levels:   mergedLevels,
            english_purposes: mergedPurposes,
          }),
        });
        if (!patchRes.ok) {
          const errData = await patchRes.json().catch(() => ({}));
          throw new Error(errData?.detail || `HTTP ${patchRes.status}`);
        }
      }

      navigate('/roadMap');
    } catch (err) {
      console.error('Error creating course:', err);
      setError('Не вдалося створити курс. Спробуйте ще раз.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Header authUser={authUser} />

      <div className={classes.page}>
        <div id="mainFlow">
          <div className={classes.pgT}>Що нового будемо вчити? 🎯</div>
          <div className={classes.pgS}>Оберіть тему та рівень для нового курсу. Завершені курси залишаться у вашому кабінеті.</div>

          {/* TOPICS */}
          <div className={classes.sec}>
            <div className={classes.secT}>Яку тему хочете вивчати?</div>
            <div className={classes.secS}>Ми підберемо уроки під новий напрям навчання.</div>
            <div className={classes.tgrid}>
              {TOPICS.map(t => (
                <div
                  key={t.id}
                  className={`${classes.tc} ${selTopic === t.id ? classes.tcSel : ''}`}
                  onClick={() => setSelTopic(t.id)}
                >
                  <div className={classes.tcCheck}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                  </div>
                  <div className={classes.tcIco} style={{ background: t.bg }}>{t.icon}</div>
                  <div className={classes.tcName}>{t.name}</div>
                  <div className={classes.tcDesc}>{t.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* LEVELS */}
          <div className={classes.sec}>
            <div className={classes.secT}>З якого рівня почати цей курс?</div>
            <div className={classes.secS}>Оберіть рівень — його можна змінити пізніше в налаштуваннях курсу.</div>

            {levelsLoading ? (
              <div className={classes.lvLoading}>Завантаження рівнів...</div>
            ) : levelsError ? (
              <div className={classes.lvErr}>{levelsError}</div>
            ) : (
              <div className={classes.lvls}>
                {levels.map(l => (
                    <div
                      key={l.id}
                      className={`${classes.lv} ${selLevel === l.id ? classes.lvSel : ''}`}
                      onClick={() => setSelLevel(l.id)}
                    >
                      {getLevelBadge(l.levels) && (
                        <div className={classes.lvBadge}>{getLevelBadge(l.levels)}</div>
                      )}
                      <div className={classes.lvInfo}>
                        <span className={classes.lvName}>{l.option}</span>
                      </div>
                      <div className={classes.lvRadio}></div>
                    </div>
                  ))}
              </div>
            )}

            <div className={classes.lvHelp}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
              Не впевнені? Оберіть приблизний рівень — його можна змінити пізніше в налаштуваннях курсу.
            </div>
          </div>

          {/* SUMMARY */}
          <div className={`${classes.sum} ${selTopic && selLevel ? classes.sumSh : ''}`}>
            <div className={classes.sumT}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
              Новий курс
            </div>
            <div className={classes.sumGrid}>
              <div className={classes.sumItem}><div className={classes.sumLabel}>Тема</div><div className={classes.sumVal}>{topicObj?.en || '—'}</div></div>
              <div className={classes.sumItem}><div className={classes.sumLabel}>Рівень</div><div className={classes.sumVal}>{levelObj?.option || '—'}</div></div>
              <div className={classes.sumItem}><div className={classes.sumLabel}>Орієнтовно</div><div className={classes.sumVal}>18 уроків</div></div>
            </div>
            <div className={classes.sumNote}>Курс буде додано до «Мої курси». Завершені курси залишаться доступними.</div>
          </div>

          {/* ERROR */}
          {error && (
            <div className={classes.errorMsg}>{error}</div>
          )}

          {/* ACTIONS */}
          <div className={classes.acts}>
            <button
              className={`${classes.btn} ${classes.btnS}`}
              onClick={() => window.history.back()}
              disabled={isLoading}
            >
              Скасувати
            </button>
            <button
              className={`${classes.btn} ${classes.btnP} ${(!selTopic || !selLevel || isLoading) ? classes.dis : ''}`}
              onClick={handleCreate}
              disabled={!selTopic || !selLevel || isLoading}
            >
              {isLoading ? 'Збереження...' : 'Створити курс'}
              {!isLoading && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateCourse;